import { ptBrCommon } from '@/lib/pt-br/messages';
import Logo from '@/components/ui/logo';
import Alert from '@/components/ui/alert';
import Input from '@/components/ui/forms/input';
import PasswordInput from '@/components/ui/forms/password-input';
import Button from '@/components/ui/button';
import * as yup from 'yup';
import { GoogleIcon } from '@/components/icons/google';
import {
  useModalAction,
  useModalState,
} from '@/components/ui/modal/modal.context';
import { Form } from '@/components/ui/forms/form';
import { useLogin, useGoogleMobileLogin, useAppleMobileLogin } from '@/framework/user';
import type { LoginUserInput } from '@/types';
import { AnonymousIcon } from '@/components/icons/anonymous-icon';
import { useRouter } from 'next/router';
import { Routes } from '@/config/routes';
import { useSettings } from '@/framework/settings';
import { useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'react-toastify';
import { useNativeApp } from '@/lib/hooks/use-native-app';
import { AppleIcon } from '@/components/icons/apple';
import { CloseIcon } from '@/components/icons/close-icon';
import { skipNextModalHistoryCleanup } from '@/components/ui/modal/history-cleanup';

const loginFormSchema = yup.object().shape({
  email: yup
    .string()
    .email('error-email-format')
    .required('error-email-required'),
  password: yup.string().required('error-password-required'),
});

function LoginForm() {
  const router = useRouter();
  const { data } = useModalState();
  const { shouldShowCheckout = false } = data || {};
  const { openModal, closeModal } = useModalAction();
  const { settings } = useSettings();
  const isCheckout = router.pathname.includes('checkout');
  const { mutate: login, isLoading, serverError, setServerError } = useLogin();
  const {
    mutate: googleMobileLogin,
    isLoading: isLoadingGoogleMobile,
    serverError: googleMobileError,
    setServerError: setGoogleMobileError,
  } = useGoogleMobileLogin();
  const {
    mutate: appleMobileLogin,
    isLoading: isLoadingAppleMobile,
    serverError: appleMobileError,
    setServerError: setAppleMobileError,
  } = useAppleMobileLogin();
  const { isNativeApp, isReady } = useNativeApp();

  const guestCheckout = settings?.guestCheckout;

  const combinedServerError = serverError || googleMobileError || appleMobileError;

  const showAppleButton = isNativeApp &&
    typeof window !== 'undefined' &&
    /iPad|iPhone|iPod|Macintosh|Mac OS/i.test(window.navigator.userAgent);

  function onSubmit({ email, password }: LoginUserInput) {
    login({
      email,
      password,
    });
  }

  const handleNativeGoogleSignIn = useCallback(
    (tokenData: {
      idToken: string;
      accessToken?: string;
      serverAuthCode?: string;
      user?: any;
    }) => {
      googleMobileLogin({
        idToken: tokenData.idToken,
        userEmail: tokenData.user?.email,
      });
    },
    [googleMobileLogin],
  );

  const handleNativeAppleSignIn = useCallback(
    (tokenData: {
      idToken: string;
      user?: any;
    }) => {
      appleMobileLogin({
        idToken: tokenData.idToken,
        userEmail: tokenData.user?.email,
      });
    },
    [appleMobileLogin],
  );

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (!isNativeApp) {
      return;
    }

    // Set up the callback for native app
    if (window.QualiAppNative) {
      window.QualiAppNative.onGoogleSignInSuccess = handleNativeGoogleSignIn;
      window.QualiAppNative.onGoogleSignInCancelled = () => {
        toast.info('Login cancelado');
      };
    }

    // Listen for custom events dispatched by injected JavaScript
    const handleGoogleSignInSuccess = (event: CustomEvent) => {
      handleNativeGoogleSignIn(event.detail);
    };

    const handleGoogleSignInCancelled = () => {
      toast.info('Login cancelado');
    };

    const handleGoogleSignInError = (event: CustomEvent) => {
      setGoogleMobileError('error-something-wrong');
      toast.error('Erro ao fazer login com Google');
    };

    window.addEventListener(
      'googleSignInSuccess',
      handleGoogleSignInSuccess as EventListener,
    );
    window.addEventListener(
      'googleSignInCancelled',
      handleGoogleSignInCancelled,
    );
    window.addEventListener(
      'googleSignInError',
      handleGoogleSignInError as EventListener,
    );

    return () => {
      window.removeEventListener(
        'googleSignInSuccess',
        handleGoogleSignInSuccess as EventListener,
      );
      window.removeEventListener(
        'googleSignInCancelled',
        handleGoogleSignInCancelled,
      );
      window.removeEventListener(
        'googleSignInError',
        handleGoogleSignInError as EventListener,
      );

      if (window.QualiAppNative) {
        window.QualiAppNative.onGoogleSignInSuccess = undefined;
        window.QualiAppNative.onGoogleSignInCancelled = undefined;
      }
    };
  }, [isReady, isNativeApp, handleNativeGoogleSignIn, setGoogleMobileError]);

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (!isNativeApp) {
      return;
    }

    // Set up the callback for native app
    if (window.QualiAppNative) {
      window.QualiAppNative.onAppleSignInSuccess = handleNativeAppleSignIn;
      window.QualiAppNative.onAppleSignInCancelled = () => {
        toast.info('Login cancelado');
      };
    }

    // Listen for custom events dispatched by injected JavaScript
    const handleAppleSignInSuccess = (event: CustomEvent) => {
      handleNativeAppleSignIn(event.detail);
    };

    const handleAppleSignInCancelled = () => {
      toast.info('Login cancelado');
    };

    const handleAppleSignInError = (event: CustomEvent) => {
      setAppleMobileError('error-something-wrong');
      toast.error('Erro ao fazer login com Apple');
    };

    window.addEventListener(
      'appleSignInSuccess',
      handleAppleSignInSuccess as EventListener,
    );
    window.addEventListener(
      'appleSignInCancelled',
      handleAppleSignInCancelled,
    );
    window.addEventListener(
      'appleSignInError',
      handleAppleSignInError as EventListener,
    );

    return () => {
      window.removeEventListener(
        'appleSignInSuccess',
        handleAppleSignInSuccess as EventListener,
      );
      window.removeEventListener(
        'appleSignInCancelled',
        handleAppleSignInCancelled,
      );
      window.removeEventListener(
        'appleSignInError',
        handleAppleSignInError as EventListener,
      );

      if (window.QualiAppNative) {
        window.QualiAppNative.onAppleSignInSuccess = undefined;
        window.QualiAppNative.onAppleSignInCancelled = undefined;
      }
    };
  }, [isReady, isNativeApp, handleNativeAppleSignIn, setAppleMobileError]);

  const handleGoogleLogin = async () => {
    if (isNativeApp) {
      try {
        if ((window as any).ReactNativeWebView?.postMessage) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'GOOGLE_SIGN_IN' }),
          );
        }
      } catch (error) {
        setGoogleMobileError('error-something-wrong');
      }
    } else {
      const endpoint = process.env.NEXT_PUBLIC_REST_API_ENDPOINT?.replace(
        /\/$/,
        '',
      );
      if (!endpoint) {
        setServerError('error-something-wrong');
        return;
      }
      window.location.assign(`${endpoint}/auth/google`);
    }
  };

  const handleAppleLogin = async () => {
    if (isNativeApp) {
      try {
        if ((window as any).ReactNativeWebView?.postMessage) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'APPLE_SIGN_IN' }),
          );
        }
      } catch (error) {
        setAppleMobileError('error-something-wrong');
      }
    } else {
      const endpoint = process.env.NEXT_PUBLIC_REST_API_ENDPOINT?.replace(
        /\/$/,
        '',
      );
      if (!endpoint) {
        setServerError('error-something-wrong');
        return;
      }
      window.location.assign(`${endpoint}/auth/apple`);
    }
  };

  return (
    <>
      {combinedServerError === 'error-needs-password-setup' ? (
        <div
          className="relative mb-6 rounded border border-accent/30 bg-accent/5 p-4 pr-10 text-sm"
          role="alert"
        >
          <button
            type="button"
            aria-label="Close"
            title="Close alert"
            onClick={() => {
              setServerError(null);
              setGoogleMobileError(null);
              setAppleMobileError(null);
            }}
            className="absolute top-3 right-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-accent transition-colors duration-200 hover:bg-gray-300 hover:bg-opacity-25 focus:bg-gray-300 focus:bg-opacity-25 focus:outline-0"
          >
            <span aria-hidden="true">
              <CloseIcon className="h-5 w-5" />
            </span>
          </button>
          <p className="font-semibold text-body-dark">
            {ptBrCommon('error-needs-password-setup')}
          </p>
          <p className="mt-1 text-body">
            {ptBrCommon('error-needs-password-setup-detail')}
          </p>
          <button
            type="button"
            onClick={() => openModal('FORGOT_VIEW')}
            className="mt-3 text-accent underline hover:no-underline"
          >
            {ptBrCommon('resend-setup-email')}
          </button>
        </div>
      ) : (
        <Alert
          variant="error"
          message={combinedServerError && ptBrCommon(combinedServerError)}
          className="mb-6"
          closeable={true}
          onClose={() => {
            setServerError(null);
            setGoogleMobileError(null);
            setAppleMobileError(null);
          }}
        />
      )}
      <Form<LoginUserInput>
        onSubmit={onSubmit}
        validationSchema={loginFormSchema}
        useFormProps={{
          defaultValues: {
            email: '',
            password: '',
          },
        }}
      >
        {({ register, formState: { errors } }) => (
          <>
            <Input
              label={"E-mail"}
              {...register('email')}
              type="email"
              variant="outline"
              className="mb-5"
              inputClassName="bg-blue-100"
              error={ptBrCommon(errors.email?.message!)}
            />
            <PasswordInput
              label={"Senha"}
              {...register('password')}
              error={ptBrCommon(errors.password?.message!)}
              variant="outline"
              className="mb-5"
              inputClassName="bg-blue-100"
              forgotPageRouteOnClick={() => openModal('FORGOT_VIEW')}
            />
            <div className="mt-8 flex gap-2">
              <Button
                type="button"
                className="w-full h-11 sm:h-12 text-accent"
                variant="secondary"
                onClick={() =>
                  openModal('REGISTER', { restrictMaxHeight: true })
                }
              >
                {"Cadastrar"}
              </Button>
              <Button
                type="submit"
                className="w-full h-11 sm:h-12"
                loading={isLoading}
                disabled={isLoading}
              >
                {"Entrar"}
              </Button>
            </div>
          </>
        )}
      </Form>
      <div className="relative flex flex-col items-center justify-center mt-8 mb-6 text-sm text-heading sm:mt-11 sm:mb-8">
        <hr className="w-full" />
        <span className="absolute -top-2.5 bg-light px-2 ltr:left-2/4 ltr:-ml-4 rtl:right-2/4 rtl:-mr-4">
          {"OU"}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 mt-2">
        <Button
          className="!bg-social-google !text-light hover:!bg-social-google-hover"
          disabled={isLoading || isLoadingGoogleMobile}
          loading={isLoadingGoogleMobile}
          onClick={handleGoogleLogin}
        >
          <GoogleIcon className="w-4 h-4 ltr:mr-3 rtl:ml-3" />
          {"Entrar com Google"}
        </Button>

        {showAppleButton && (
          <Button
            className="!bg-black !text-white hover:!bg-gray-900"
            disabled={isLoading || isLoadingAppleMobile}
            loading={isLoadingAppleMobile}
            onClick={handleAppleLogin}
          >
            <AppleIcon className="w-4 h-4 ltr:mr-3 rtl:ml-3" />
            Entrar com Apple
          </Button>
        )}

        {/* TODO: Implement OTP login in the future */}
        {/* <Button
          className="h-11 w-full !bg-social-google !text-light hover:!bg-gray-600 sm:h-12"
          disabled={isLoading}
          onClick={() => openModal('OTP_LOGIN')}
        >
          <MobileIcon className="h-5 text-light ltr:mr-2 rtl:ml-2" />
          {"Entrar com número de celular"}
        </Button> */}

        {isCheckout && guestCheckout && shouldShowCheckout && (
          <Button
            className="h-11 w-full !bg-pink-700 !text-light hover:!bg-pink-800 sm:h-12"
            disabled={isLoading}
            onClick={() => {
              skipNextModalHistoryCleanup();
              flushSync(() => {
                closeModal();
              });
              void router.replace(Routes.checkoutGuest);
            }}
          >
            <AnonymousIcon className="h-6 text-light ltr:mr-2 rtl:ml-2" />
            {"Finalizar Compra como Convidado"}
          </Button>
        )}
      </div>
      {/* <div className="relative flex flex-col items-center justify-center mt-8 mb-6 text-sm text-heading sm:mt-11 sm:mb-8">
        <hr className="w-full" />
      </div>
      <div className="text-sm text-center text-body sm:text-base">
        {"Não tem uma conta?"}{' '}
        <button
          onClick={() => openModal('REGISTER')}
          className="font-semibold underline transition-colors duration-200 text-accent hover:text-accent-hover hover:no-underline focus:text-accent-hover focus:no-underline focus:outline-0 ltr:ml-1 rtl:mr-1"
        >
          {"Cadastrar"}
        </button>
      </div> */}
    </>
  );
}

export default function LoginView() {
  return (
    <div className="flex h-full min-h-screen w-screen flex-col justify-center bg-light py-6 px-5 sm:p-8 md:h-auto md:min-h-0 md:max-w-[480px] md:rounded-xl">
      <div className="flex justify-center">
        <Logo />
      </div>
      <p className="mt-4 mb-8 text-sm text-center text-body sm:mt-5 sm:mb-10 md:text-base">
        {"Faça login com seu e-mail e senha"}
      </p>
      <LoginForm />
    </div>
  );
}
